import { inject, Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { filter, switchMap, take } from 'rxjs/operators';
import {
  AddMedServiceWebRequest,
  IcdCode,
  IcdCodeType,
  MedServiceDto,
  MedServiceHeaderRowItemSearchResult,
  MhsConfigurationApiClient,
  UpdateMedServiceWebRequest,
} from '../../../core/api/mhsConfigurationApiClient';
import { AuthStore } from '../../../core/authentication/authStore';
import { UsertStore } from '../../../core/context/userStore';

@Injectable({
  providedIn: 'root',
})
export class ProceduresService {
  private readonly configurationApiClient = inject(MhsConfigurationApiClient);
  private readonly authStore = inject(AuthStore);
  private readonly userStore = inject(UsertStore);

  /** Zwraca procedurę po id. */
  get(id: number): Observable<MedServiceDto> {
    if (this.authStore.accessToken() === '') {
      return EMPTY;
    }

    return this.userStore.currentTenant$.pipe(
      filter((tenant) => tenant !== null),
      take(1),
      switchMap(() => this.configurationApiClient.medServices_Get(id)),
    );
  }

  /** Dodaje nową procedurę. */
  add(body: AddMedServiceWebRequest): Observable<MedServiceDto> {
    if (this.authStore.accessToken() === '') {
      return EMPTY;
    }

    return this.userStore.currentTenant$.pipe(
      filter((tenant) => tenant !== null),
      take(1),
      switchMap(() => this.configurationApiClient.medServices_Add(body)),
    );
  }
  hide(id: number): Observable<void> {
    if (this.authStore.accessToken() === '') {
      return EMPTY;
    }

    return this.userStore.currentTenant$.pipe(
      filter((tenant) => tenant !== null),
      take(1),
      switchMap(() => this.configurationApiClient.medServices_Hide(id)),
    );
  }
  reveal(id: number): Observable<void> {
    if (this.authStore.accessToken() === '') {
      return EMPTY;
    }

    return this.userStore.currentTenant$.pipe(
      filter((tenant) => tenant !== null),
      take(1),
      switchMap(() => this.configurationApiClient.medServices_Unhide(id)),
    );
  }
  /** Aktualizuje procedurę. */
  update(body: UpdateMedServiceWebRequest): Observable<MedServiceDto> {
    if (this.authStore.accessToken() === '') {
      return EMPTY;
    }

    return this.userStore.currentTenant$.pipe(
      filter((tenant) => tenant !== null),
      take(1),
      switchMap(() => this.configurationApiClient.medServices_Update(body)),
    );
  }
  queryCode(codeType: IcdCodeType, phrase: string, top: number): Observable<IcdCode[]> {
    if (this.authStore.accessToken() === '') {
      return EMPTY;
    }
    return this.userStore.currentTenant$.pipe(
      filter((tenant) => tenant !== null),
      take(1),
      switchMap(() => this.configurationApiClient.icdCode_GetByPhrase(codeType, phrase, top)),
    );
  }
  /** Zwraca stronicowaną listę procedur. */
  search(
    type?: string,
    unitId?: string,
    divisionId?: string,
    phrase?: string,
    withContrast?: string,
    withSpine?: string,
    withPrePostContrast?: string,
    pageIndex = 1,
    pageSize = 10,
    orderBy?: string,
    isOrderByAsc?: boolean,
  ): Observable<MedServiceHeaderRowItemSearchResult> {
    if (this.authStore.accessToken() === '') {
      return EMPTY;
    }

    return this.userStore.currentTenant$.pipe(
      filter((tenant) => tenant !== null),
      take(1),
      switchMap(() =>
        this.configurationApiClient.medServices_Search(
          type,
          unitId,
          divisionId,
          phrase,
          withContrast,
          withSpine,
          withPrePostContrast,
          pageIndex,
          pageSize,
          orderBy,
          isOrderByAsc,
        ),
      ),
    );
  }
}
